/* eslint-disable no-param-reassign, no-bitwise, class-methods-use-this */

import { ThemeProperties, TypographySizes } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/version4';
import type { ConversionInput, ThemeConverter as IThemeConverter } from './types';
import {
  ThemeButtonVariantKey,
  ThemeColorName,
  ThemePropsSchema,
  type ThemeButtonVariant,
  type ThemeProps,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { Block } from '@mutiny-pkg/champaign-schemas/src/schemas/blocks/Block.types';
import { BlockType } from '@mutiny-pkg/champaign-schemas/src/schemas/blocks/Types';
import { getColorName, isValidColor } from '@mutiny-pkg/microsite-conversion/src/utils/colorVariable';
import { ThemePropertiesSchema } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties';
import { parseDirectionalValue } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/tailwindUtils';

function stripWhitespace(str: string): string {
  return str.replace(/\s/g, '');
}

const GOOGLE_FONTS_MAP: Record<string, string> = {
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap',
  Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
  'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap',
};

const FONT_FALLBACKS: Record<string, string> = {
  Inter: 'Inter, system-ui, -apple-system, sans-serif',
  Roboto: 'Roboto, system-ui, -apple-system, sans-serif',
  'Open Sans': 'Open Sans, system-ui, -apple-system, sans-serif',
};

function formatFontWeightClassName(fontWeight: string | undefined, defaultWeight: string): string {
  if (!fontWeight) return `font-[${defaultWeight}]`;
  const parts = fontWeight.split(/\s+/);
  if (parts.length > 1) return `font-[${defaultWeight}]`;
  return `font-[${parts[0]}]`;
}

/**
 * Valid CSS border style values
 */
const VALID_BORDER_STYLES = new Set([
  'none',
  'hidden',
  'dotted',
  'dashed',
  'solid',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
]);

/**
 * Validates if a border style is a valid CSS border-style value
 * Returns 'none' if invalid, otherwise returns the original value
 */
function validateBorderStyle(borderStyle: string | undefined): string {
  if (!borderStyle) return 'none';
  const normalized = borderStyle.toLowerCase().trim();
  return VALID_BORDER_STYLES.has(normalized) ? normalized : 'none';
}

/**
 * Converts Champaign ThemeProperties to microsite theme system
 *
 * Maps from Champaign's nested theme properties to microsite's comprehensive
 * dot-notation theme structure with colors, typography, variants, and more.
 *
 * Preserves original hex colors, adds structured font families, component variants,
 * and comprehensive design tokens for modern B2B theming.
 */
export class ThemeConverter implements IThemeConverter {
  convert(input: ConversionInput): ThemeProps {
    if (input.theme.definition) {
      return input.theme.definition;
    }
    return this.convertThemeProperties(input.theme.properties!, input.pageLayout.blocks);
  }

  convertThemeProperties(themeProperties: ThemeProperties, blocks: Block[] = []): ThemeProps {
    // No need to convert the properties if it's already in the new properties schema
    if (!ThemePropertiesSchema.latestVersionSchema.safeParse(themeProperties).success) {
      return themeProperties as ThemeProps;
    }
    const oldThemeProperties = themeProperties as ThemeProperties;
    const themeProps: ThemeProps = {};
    // 1. COLORS - New structured color system
    this.convertColors(blocks, oldThemeProperties, themeProps);
    // 2. FONTS ARRAY - All unique fonts from the theme
    this.convertFonts(oldThemeProperties, themeProps);
    // 3. DESIGN TOKENS - Tracking, leading, radius, shadows
    this.convertDesignTokens(oldThemeProperties, themeProps);
    // 4. BREAKPOINTS - Responsive breakpoints
    this.convertBreakpoints(themeProps);
    // 5. COMPONENT VARIANTS - Button and text variants
    this.convertVariants(oldThemeProperties, themeProps);
    // Validate the theme props
    return ThemePropsSchema.parse(themeProps);
  }

  /**
   * Convert colors to the new structured format preserving original hex values
   */
  private convertColors(blocks: Block[], oldthemeProps: ThemeProperties, convertedThemeProps: ThemeProps): void {
    // Base colors - structured with color and name, preserving original hex values
    convertedThemeProps['colors.primary'] = {
      color: oldthemeProps.color.primary,
      name: ThemeColorName.Primary,
    };

    convertedThemeProps['colors.secondary'] = {
      color: oldthemeProps.color.secondary,
      name: ThemeColorName.Secondary,
    };

    convertedThemeProps['colors.tertiary'] = {
      color: oldthemeProps.color.tertiary,
      name: ThemeColorName.Tertiary,
    };

    // Find all unique colors
    // Collect all unique colors from blocks' options using a Set
    const uniqueColors = new Set<string>();

    for (const block of blocks) {
      switch (block.blockConfig.type) {
        case BlockType.Cta:
        case BlockType.Hero:
        case BlockType.Footer:
        case BlockType.NavBar:
        case BlockType.LogoBar:
        case BlockType.ValueProp:
        case BlockType.Spotlight:
        case BlockType.Testimonial:
        case BlockType.ResourceLibrary: {
          if (block.blockConfig.options?.style?.backgroundColor)
            uniqueColors.add(block.blockConfig.options.style.backgroundColor);
          break;
        }
        default: {
          break;
        }
      }
    }

    // Add the additional colors from the old theme properties
    if (oldthemeProps.additionalColors && oldthemeProps.additionalColors.length > 0) {
      for (const color of oldthemeProps.additionalColors) {
        if (isValidColor(color)) {
          uniqueColors.add(color);
        }
      }
    }

    convertedThemeProps['colors.additional'] = Array.from(uniqueColors).map((color) => {
      return {
        color,
        name: getColorName(color),
      };
    });

    // Text colors
    convertedThemeProps['colors.dark'] = {
      color: oldthemeProps.color.textDark,
      name: 'Primary Text',
    };

    convertedThemeProps['colors.light'] = {
      color: oldthemeProps.color?.textLight,
      name: 'Inverse Text',
    };

    convertedThemeProps['logo.light'] = oldthemeProps.logos.light;
    convertedThemeProps['logo.dark'] = oldthemeProps.logos.dark;
  }

  /**
   * Convert fonts using actual fontFaces array from ThemePropertiesV4
   */
  private convertFonts(props: ThemeProperties, themeProps: ThemeProps): void {
    const fonts: ThemeProps['fonts'] = [];

    // First priority: Use the actual fontFaces array from ThemePropertiesV4
    if (props.fontFaces && Array.isArray(props.fontFaces)) {
      // Group font faces by font family
      const fontGroups = new Map<string, ThemeProperties['fontFaces']>();

      props.fontFaces.forEach((fontFace: ThemeProperties['fontFaces'][number]) => {
        const family = fontFace.fontFamily;
        if (!fontGroups.has(family)) {
          fontGroups.set(family, []);
        }
        fontGroups.get(family)!.push(fontFace);
      });

      // Convert each font family group to QKit format
      fontGroups.forEach((variants, fontFamily) => {
        fonts.push({
          family: fontFamily, // Use exact font family from fontFaces
          name: this.generateFontName(fontFamily),
          variants: variants.map((variant) => ({
            url: variant.src || '',
            weight: this.mapFontWeight(variant.fontWeight, props.fontWeight) || 'normal',
            style: variant.fontStyle || 'normal',
          })),
        });
      });
    }

    // Fallback: Extract unique fonts from typography if no fontFaces
    if (fonts.length === 0) {
      const uniqueFonts = new Set<string>();

      // Extract all unique font families from typography settings
      const bodyFont = props.typography.body.fontFamily;
      const ctaFont = props.typography.cta.fontFamily;
      const linkFont = props.typography.link.fontFamily;
      const h1Font = props.typography.h1.fontFamily;
      const h2Font = props.typography.h2.fontFamily;
      const h3Font = props.typography.h3.fontFamily;
      const h4Font = props.typography.h4.fontFamily;

      // Add all unique fonts to the set
      [bodyFont, ctaFont, linkFont, h1Font, h2Font, h3Font, h4Font].forEach((font) => {
        if (font && font.trim() !== '') {
          uniqueFonts.add(font);
        }
      });

      // Convert each unique font to the fonts array format
      uniqueFonts.forEach((fontFamily) => {
        fonts.push({
          family: this.buildFontFamilyString(fontFamily),
          name: this.generateFontName(fontFamily),
          variants: this.generateFontVariants(fontFamily),
        });
      });
    }

    // Set the fonts array on the theme
    themeProps.fonts = fonts;
  }

  /**
   * Map font weight from ThemePropertiesV4 fontWeight object
   */
  private mapFontWeight(fontWeight: string, fontWeightMap?: any): string {
    if (!fontWeightMap) return fontWeight;

    // If the fontWeight is a key in the fontWeight map, use the mapped value
    if (fontWeightMap[fontWeight]) {
      return fontWeightMap[fontWeight];
    }

    return fontWeight;
  }

  /**
   * Convert design tokens - tracking, leading, radius, shadows
   */
  private convertDesignTokens(_props: ThemeProperties, themeProps: ThemeProps): void {
    // Shadows
    themeProps['shadow.sm'] = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
    themeProps['shadow.md'] = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
    themeProps['shadow.lg'] = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
    themeProps['shadow.xl'] = '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)';
    themeProps['shadow.2xl'] = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
  }

  /**
   * Convert breakpoints
   */
  private convertBreakpoints(themeProps: ThemeProps): void {
    themeProps['breakpoints.sm'] = '40rem';
    themeProps['breakpoints.md'] = '48rem';
    themeProps['breakpoints.lg'] = '64rem';
    // themeProps['breakpoints.xl'] = '80rem';
    // themeProps['breakpoints.2xl'] = '96rem';
  }

  /**
   * Convert component variants - button and text variants
   */
  private convertVariants(props: ThemeProperties, themeProps: ThemeProps): void {
    // Button variants
    this.convertButtonVariants(props, themeProps);

    // Text variants
    this.convertTextVariants(props, themeProps);
  }

  private convertTransition(transition: string): string {
    const tailwindValue = transition.replaceAll(' ', '_');
    return `[transition:${tailwindValue}]`;
  }

  private extractButtonFontProperties(props: ThemeProperties) {
    // Font properties come from typography configuration since CTAs don't specify fonts
    // Use CTA typography or fallback to body typography
    const ctaTypography = props.typography.cta;
    const ctaFont = ctaTypography.fontFamily;
    return {
      // Get the exact font family name from fontFaces, not a generated name
      buttonFontFamily: this.getExactFontFamily(ctaFont, props.fontFaces),
      buttonFontSize: ctaTypography.fontSize,
      buttonLineHeight: ctaTypography.lineHeight,
      buttonFontWeight: ctaTypography.fontWeight,
      buttonLetterSpacing: ctaTypography.letterSpacing,
    };
  }

  private convertCta(props: ThemeProperties, variantKey: ThemeButtonVariantKey): ThemeButtonVariant {
    let ctaType = variantKey.replace('variants.button.', '');
    // For Ghost, outline and link, derive from the primary CTA type
    if (!(ctaType in props.cta)) {
      ctaType = 'primary';
    }
    const cta = props.cta[ctaType as keyof ThemeProperties['cta']];
    const { buttonFontFamily, buttonFontSize, buttonLineHeight, buttonFontWeight, buttonLetterSpacing } =
      this.extractButtonFontProperties(props);

    const fontFamily = buttonFontFamily
      ? `font-${this.getGeneratedFontName(buttonFontFamily, props.fontFaces)}`
      : undefined;

    // Link variant is a bit special
    if (variantKey === ThemeButtonVariantKey.Link) {
      return {
        base: {
          color: `text-[${stripWhitespace(cta.backgroundColor)}]`,
          fontFamily,
          textDecoration: 'underline',
          transition: '',
        },
        hover: {
          opacity: 'opacity-[0.8]',
        },
      };
    }

    const validatedBorderStyle = validateBorderStyle(cta.borderStyle);
    const parsedBorderWidth = parseDirectionalValue(cta.borderWidth);
    // TODO We do not support mutli-directional border width values yet
    const borderWidth = parsedBorderWidth ? parsedBorderWidth.top : cta.borderWidth;
    const base: Record<string, string | undefined> = {
      color: `text-[${stripWhitespace(cta.color)}]`,
      padding: `pt-[${cta.paddingTop}] pr-[${cta.paddingRight}] pb-[${cta.paddingBottom}] pl-[${cta.paddingLeft}]`,
      borderRadius: `rounded-[${cta.borderRadius}]`,
      border: `border-[${borderWidth}] border-${validatedBorderStyle} border-[${stripWhitespace(cta.borderColor)}]`,

      fontWeight: `font-[${buttonFontWeight}]`,
      transition: this.convertTransition(cta.transition),
    };

    const hover: Record<string, string | undefined> = {
      backgroundColor: `bg-[${stripWhitespace(cta.backgroundColor)}]`,
      color: `text-[${stripWhitespace(cta.color)}]`,
    };

    // Main variants have more properties set
    if (
      variantKey === ThemeButtonVariantKey.Primary ||
      variantKey === ThemeButtonVariantKey.Secondary ||
      variantKey === ThemeButtonVariantKey.Tertiary
    ) {
      base.fontFamily = fontFamily;
      base.backgroundColor = `bg-[${stripWhitespace(cta.backgroundColor)}]`;
      base.fontSize = `text-[${buttonFontSize}]`;
      base.lineHeight = `leading-[${buttonLineHeight}]`;
      base.letterSpacing = `tracking-[${buttonLetterSpacing}]`;

      hover.backgroundColor = `bg-[${stripWhitespace(cta.hoverBackgroundColor)}]`;
      hover.color = `text-[${stripWhitespace(cta.hoverColor)}]`;
    }

    return { base, hover };
  }

  /**
   * Convert button variants using actual CTA properties from ThemePropertiesV4
   */
  private convertButtonVariants(props: ThemeProperties, themeProps: ThemeProps): void {
    themeProps[ThemeButtonVariantKey.Primary] = this.convertCta(props, ThemeButtonVariantKey.Primary);
    themeProps[ThemeButtonVariantKey.Secondary] = this.convertCta(props, ThemeButtonVariantKey.Secondary);
    themeProps[ThemeButtonVariantKey.Tertiary] = this.convertCta(props, ThemeButtonVariantKey.Tertiary);
    themeProps[ThemeButtonVariantKey.Outline] = this.convertCta(props, ThemeButtonVariantKey.Outline);
    themeProps[ThemeButtonVariantKey.Ghost] = this.convertCta(props, ThemeButtonVariantKey.Ghost);
    themeProps[ThemeButtonVariantKey.Link] = this.convertCta(props, ThemeButtonVariantKey.Link);
  }

  /**
   * Get exact font family from fontFaces array, preserving original font definitions
   */
  private getExactFontFamily(fontFamily: string, fontFaces?: ThemeProperties['fontFaces']): string | undefined {
    if (!fontFaces || !Array.isArray(fontFaces)) {
      return this.buildFontFamilyString(fontFamily);
    }

    // Find the font in fontFaces array
    const fontFace = fontFaces.find(
      (face) => stripWhitespace(face.fontFamily).toLowerCase() === stripWhitespace(fontFamily).toLowerCase(),
    );
    if (fontFace) {
      // Use the exact font family from fontFaces
      return fontFace.fontFamily;
    }

    return undefined;
  }

  private getGeneratedFontName(fontFamily: string, fontFaces?: ThemeProperties['fontFaces']): string | undefined {
    const exactFontFamily = this.getExactFontFamily(fontFamily, fontFaces);
    if (!exactFontFamily) {
      return undefined;
    }
    return exactFontFamily
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Convert text variants with mapped font families from Champaign typography
   */
  private convertTextVariants(props: ThemeProperties, themeProps: ThemeProps): void {
    // Helper to get font family for a typography type
    const getFontFamilyForType = (typographyType: keyof ThemeProperties['typography']): string | undefined => {
      const { fontFamily } = props.typography[typographyType];
      if (!fontFamily) return undefined;

      const exactFontFamily = this.getExactFontFamily(fontFamily, props.fontFaces);
      if (!exactFontFamily) return undefined;

      const generatedName = this.getGeneratedFontName(exactFontFamily, props.fontFaces);
      return generatedName ? `font-${generatedName}` : undefined;
    };

    const convertTypography = (
      typography: ThemeProperties['typography'][keyof ThemeProperties['typography']],
      type: keyof ThemeProperties['typography'],
    ) => {
      return {
        color: `text-[${stripWhitespace(props.color.primary)}]`,
        fontFamily: getFontFamilyForType(type),
        fontWeight: formatFontWeightClassName(typography.fontWeight, '400'),
        fontSize: `text-[${typography.fontSize}]`,
        lineHeight: `leading-[${typography.lineHeight}]`,
        letterSpacing: `tracking-[${typography.letterSpacing}]`,
      };
    };

    themeProps['variants.text.body'] = convertTypography(props.typography.body, TypographySizes.Body);
    themeProps['variants.text.heading1'] = convertTypography(props.typography.h1, TypographySizes.H1);
    themeProps['variants.text.heading2'] = convertTypography(props.typography.h2, TypographySizes.H2);
    themeProps['variants.text.heading3'] = convertTypography(props.typography.h3, TypographySizes.H3);
    themeProps['variants.text.heading4'] = convertTypography(props.typography.h4, TypographySizes.H4);
    // There is no overline typography in the old theme, so we use default values
    themeProps['variants.text.overline'] = {
      fontWeight: 'font-[500]',
      fontSize: 'text-[0.875rem]',
      letterSpacing: 'tracking-[0.05em]',
      textTransform: 'uppercase',
    };
  }

  /**
   * Generate font name for theme fonts array
   */
  private generateFontName(fontFamily: string): string {
    // Convert font family to kebab-case name
    return fontFamily
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Build font family string with fallbacks
   */
  private buildFontFamilyString(fontName: string): string {
    return FONT_FALLBACKS[fontName] || `${fontName}, system-ui, -apple-system, sans-serif`;
  }

  /**
   * Generate font variants with Google Fonts URLs
   */
  private generateFontVariants(fontName: string): Array<{ url: string; weight: string; style: string }> {
    const url = GOOGLE_FONTS_MAP[fontName] || '';

    return [
      { url, weight: 'normal', style: 'normal' },
      { url, weight: 'bold', style: 'normal' },
    ];
  }
}
